'use client';

import { useState } from 'react';
import Link from 'next/link';
import { IconArrowRight, IconChevronRight } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { formatHourlyRate } from '@/lib/utils/currency';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getAvailableGPUs } from '@/lib/data/gpu-products';

interface GPUSidebarWidgetsProps {
  className?: string;
}

export function GPUSidebarWidgets({ className }: GPUSidebarWidgetsProps) {
  return (
    <div className={cn('space-y-3', className)}>
      <HowItWorksWidget />
      <QuickCompareWidget />
      <NeedHelpWidget />
    </div>
  );
}

const widgetShell = 'bg-card text-card-foreground rounded-2xl border border-border p-5';

function HowItWorksWidget() {
  const steps = [
    {
      number: '1',
      title: 'Choose a GPU and region',
      description: 'The form shows the hourly rate before you create',
    },
    {
      number: '2',
      title: 'Start the machine',
      description: 'Start-up takes a few minutes',
    },
    {
      number: '3',
      title: 'Connect',
      description: 'Remote Desktop, or Parsec / Moonlight (third-party)',
    },
  ];

  return (
    <div className={widgetShell}>
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-4">
        How It Works
      </p>
      <div className="space-y-4">
        {steps.map((step) => (
          <div key={step.number} className="flex gap-3">
            <div className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              <span className="text-xs font-bold">{step.number}</span>
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <p className="text-sm font-medium text-foreground leading-tight">{step.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
            </div>
          </div>
        ))}
      </div>
      <Link
        href="/how-it-works"
        className="inline-flex items-center gap-1.5 mt-5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        Learn more
        <IconArrowRight className="size-3" />
      </Link>
    </div>
  );
}

function QuickCompareWidget() {
  // Rentable products only: a withdrawn or unreleased card has no rate to compare.
  const rentable = getAvailableGPUs();
  const [gpu1, setGpu1] = useState(rentable[0]?.id || '');
  const [gpu2, setGpu2] = useState(rentable[1]?.id || '');

  const selectedGpu1 = rentable.find((g) => g.id === gpu1);
  const selectedGpu2 = rentable.find((g) => g.id === gpu2);

  const picker = (value: string, onChange: (v: string) => void, exclude: string) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full h-10 rounded-lg">
        <SelectValue placeholder="Select GPU" />
      </SelectTrigger>
      <SelectContent>
        {rentable.map((gpu) => (
          <SelectItem key={gpu.id} value={gpu.id} disabled={gpu.id === exclude}>
            {gpu.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const summary = (gpu: (typeof rentable)[number] | undefined) =>
    gpu && (
      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-xs text-muted-foreground">
          {gpu.vram}GB VRAM · {gpu.vcpus} vCPUs
        </span>
        <span className="text-sm font-semibold text-foreground tabular-nums">
          {formatHourlyRate(gpu.basePrice)}
        </span>
      </div>
    );

  return (
    <div className={widgetShell}>
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-4">
        Quick Compare
      </p>

      <div className="space-y-4">
        <div>
          {picker(gpu1, setGpu1, gpu2)}
          {summary(selectedGpu1)}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <span className="text-[10px] font-semibold text-muted-foreground uppercase">vs</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <div>
          {picker(gpu2, setGpu2, gpu1)}
          {summary(selectedGpu2)}
        </div>

        {selectedGpu1 && selectedGpu2 && (
          <div className="pt-3 border-t border-border">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Difference in base rate</span>
              <span className="font-medium text-foreground tabular-nums">
                {selectedGpu1.basePrice > selectedGpu2.basePrice ? '+' : ''}
                {formatHourlyRate(selectedGpu1.basePrice - selectedGpu2.basePrice)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NeedHelpWidget() {
  return (
    <div className={cn(widgetShell, 'transition-colors hover:border-primary/30')}>
      <Badge variant="outline" className="mb-3 text-[10px] uppercase tracking-wider">
        Support
      </Badge>
      <p className="text-base font-semibold text-foreground mb-1">Not sure which size?</p>
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        Tell us what you want to run and we will suggest a GPU and region.
      </p>
      <Button asChild variant="outline" className="w-full rounded-xl h-10 font-medium text-sm">
        <Link href="/help">
          Contact Support
          <IconChevronRight className="size-4" />
        </Link>
      </Button>
    </div>
  );
}

// Featured GPU card for the bento layout
interface FeaturedGPUCardProps {
  title: string;
  subtitle: string;
  specs: string[];
  price: string;
  ctaText: string;
  /** Link into the create form, built with `machineCreateHref()`. */
  href: string;
  badge?: string;
  className?: string;
}

export function FeaturedGPUCard({
  title,
  subtitle,
  specs,
  price,
  ctaText,
  href,
  badge = 'Popular',
  className,
}: FeaturedGPUCardProps) {
  return (
    <div
      className={cn(
        'relative rounded-2xl p-6 overflow-hidden bg-card text-card-foreground border border-border',
        className
      )}
    >
      {/* Brand cue: a soft primary glow in the corner, token-mixed so it follows the palette */}
      <div aria-hidden className="orb-primary -top-24 -right-24 h-64 w-64" />

      {badge && (
        <Badge className="absolute top-5 right-5 text-[10px] uppercase tracking-wider">{badge}</Badge>
      )}

      <div className="relative flex flex-col h-full">
        <div className="flex-1">
          <p className="text-xs font-medium uppercase tracking-wide mb-2 text-muted-foreground pr-24">
            {subtitle}
          </p>
          <h3 className="text-2xl font-bold mb-4 text-foreground">{title}</h3>
          <div className="flex flex-wrap gap-2">
            {specs.map((spec) => (
              <span
                key={spec}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-muted text-foreground"
              >
                {spec}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 pt-5 mt-5 border-t border-border">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider mb-0.5 text-muted-foreground">
              From
            </p>
            <p className="text-2xl font-bold tabular-nums text-foreground">{price}</p>
          </div>
          <Button asChild className="h-11 px-5 rounded-xl font-semibold text-sm">
            <Link href={href}>
              {ctaText}
              <IconChevronRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
